// Notification Delivery System for SECRET Program
// Supports SMS via Twilio and Email via Resend

import { supabase } from './supabase'

interface NotificationPayload {
  userId: string
  type: 'event_announcement' | 'order_confirmation' | 'order_status' | 'transfer_received' | 'system'
  title: string
  message: string
  channels: ('sms' | 'email' | 'push')[]
  eventId?: string
  orderId?: string
  metadata?: Record<string, any>
}

interface SMSPayload {
  to: string
  message: string
}

interface EmailPayload {
  to: string
  subject: string
  html: string
  text?: string
}

interface PushPayload {
  userId: string
  title: string
  body: string
  data?: Record<string, any>
}

// SMS Templates
const SMS_TEMPLATES = {
  event_announcement: (data: any) => 
    `🦞 SECRET Alert: New popup event "${data.eventTitle}" at ${data.location} on ${data.date}. Open the app to pre-order exclusive items!`,
  
  order_confirmation: (data: any) => 
    `✅ Order ${data.orderNumber} confirmed! Total: $${data.total}. You'll get pickup details soon. Thanks for choosing SECRET!`,
  
  order_status: (data: any) => {
    switch (data.status) {
      case 'preparing':
        return `👨‍🍳 Your SECRET order ${data.orderNumber} is being prepared! We'll notify you when ready for pickup.`
      case 'ready':
        return `🎉 Order ${data.orderNumber} is ready! Show your QR code at ${data.location} for pickup. Pickup by ${data.pickupTime}.`
      case 'completed':
        return `✅ Order ${data.orderNumber} complete! Thanks for the SECRET experience. Rate us in the app!`
      default:
        return `📋 Update: Order ${data.orderNumber} status changed to ${data.status}.`
    }
  },
  
  transfer_received: (data: any) => 
    `🎁 You've received order ${data.orderNumber} from ${data.fromName}! Open the app to view details and pickup instructions.`,
  
  system: (data: any) => data.message
}

// Email Templates  
const EMAIL_TEMPLATES = {
  event_announcement: (data: any) => ({
    subject: `🦞 New SECRET Event: ${data.eventTitle}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #000; color: #fff; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #8a3134; font-size: 32px; margin: 0;">SECRET</h1>
          <p style="color: #888; margin: 5px 0;">Exclusive Tailtations Experience</p>
        </div>
        
        <div style="background: #1a1a1a; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h2 style="color: #8a3134; margin-top: 0;">New Popup Event!</h2>
          <h3 style="color: #fff; margin: 10px 0;">${data.eventTitle}</h3>
          <p style="color: #ccc; line-height: 1.6;">${data.eventDescription}</p>
          
          <div style="margin: 20px 0; padding: 15px; background: #2a2a2a; border-radius: 6px;">
            <p style="margin: 5px 0; color: #fff;"><strong>📍 Location:</strong> ${data.location}</p>
            <p style="margin: 5px 0; color: #fff;"><strong>📅 Date:</strong> ${data.date}</p>
            <p style="margin: 5px 0; color: #fff;"><strong>⏰ Time:</strong> ${data.time}</p>
          </div>
          
          <a href="${data.eventUrl}" style="display: inline-block; background: #8a3134; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; margin-top: 15px;">
            View SECRET Menu
          </a>
        </div>
        
        <p style="color: #888; text-align: center; font-size: 12px;">
          You're receiving this because you're a SECRET member. Manage preferences in the app.
        </p>
      </div>
    `,
    text: `SECRET Alert: New popup event "${data.eventTitle}" at ${data.location} on ${data.date} at ${data.time}. View the SECRET menu: ${data.eventUrl}`
  }),

  order_confirmation: (data: any) => ({
    subject: `Order Confirmed: ${data.orderNumber}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #000; color: #fff; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #8a3134; font-size: 32px; margin: 0;">SECRET</h1>
          <div style="background: #1a7e38; color: #fff; padding: 10px; border-radius: 6px; margin-top: 15px;">
            <h2 style="margin: 0; font-size: 18px;">✅ Order Confirmed</h2>
          </div>
        </div>
        
        <div style="background: #1a1a1a; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #fff; margin-top: 0;">Order #${data.orderNumber}</h3>
          <p style="color: #ccc;">Placed on ${data.orderDate}</p>
          
          <div style="margin: 20px 0;">
            <h4 style="color: #8a3134; margin-bottom: 15px;">Items Ordered:</h4>
            ${data.items.map(item => `
              <div style="border-bottom: 1px solid #333; padding: 10px 0;">
                <div style="display: flex; justify-content: space-between; align-items: start;">
                  <div>
                    <strong style="color: #fff;">${item.name}</strong>
                    <p style="color: #999; margin: 5px 0; font-size: 14px;">Qty: ${item.quantity} × $${item.unitPrice}</p>
                    ${item.instructions ? `<p style="color: #888; font-size: 12px; font-style: italic;">Note: ${item.instructions}</p>` : ''}
                  </div>
                  <strong style="color: #8a3134;">$${item.total}</strong>
                </div>
              </div>
            `).join('')}
            
            <div style="text-align: right; margin-top: 15px; padding-top: 15px; border-top: 2px solid #8a3134;">
              <p style="color: #fff; font-size: 18px; margin: 0;"><strong>Total: $${data.total}</strong></p>
            </div>
          </div>
          
          <div style="background: #2a2a2a; padding: 15px; border-radius: 6px; margin: 20px 0;">
            <h4 style="color: #8a3134; margin: 0 0 10px 0;">Pickup Details:</h4>
            <p style="margin: 5px 0; color: #fff;">${data.eventTitle}</p>
            <p style="margin: 5px 0; color: #ccc;">${data.location}</p>
            <p style="margin: 5px 0; color: #ccc;">${data.eventDate}</p>
          </div>
          
          <p style="color: #ccc; line-height: 1.6;">
            We'll notify you when your order is being prepared and ready for pickup. 
            Make sure to bring your QR code!
          </p>
          
          <a href="${data.orderUrl}" style="display: inline-block; background: #8a3134; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; margin-top: 15px;">
            Track Your Order
          </a>
        </div>
      </div>
    `,
    text: `Order ${data.orderNumber} confirmed! Total: $${data.total}. Pickup at ${data.location} on ${data.eventDate}. Track: ${data.orderUrl}`
  }),

  order_status: (data: any) => ({
    subject: `Order Update: ${data.orderNumber}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #000; color: #fff; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #8a3134; font-size: 32px; margin: 0;">SECRET</h1>
        </div>
        
        <div style="background: #1a1a1a; padding: 20px; border-radius: 8px;">
          <h3 style="color: #fff; margin-top: 0;">Order #${data.orderNumber}</h3>
          
          <div style="background: ${data.status === 'ready' ? '#1a7e38' : '#8a3134'}; padding: 15px; border-radius: 6px; margin: 20px 0; text-align: center;">
            <h2 style="margin: 0; color: #fff;">${data.statusText}</h2>
          </div>
          
          ${data.status === 'ready' ? `
            <div style="background: #2a2a2a; padding: 15px; border-radius: 6px; margin: 20px 0;">
              <h4 style="color: #8a3134; margin: 0 0 10px 0;">🎉 Ready for Pickup!</h4>
              <p style="color: #fff; margin: 5px 0;">Location: ${data.location}</p>
              <p style="color: #fff; margin: 5px 0;">Pickup by: ${data.pickupTime}</p>
              <p style="color: #ccc; font-size: 14px; margin-top: 10px;">Show your QR code to staff for pickup.</p>
            </div>
          ` : ''}
          
          <a href="${data.orderUrl}" style="display: inline-block; background: #8a3134; color: #fff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
            View Order Details
          </a>
        </div>
      </div>
    `,
    text: `Order ${data.orderNumber} update: ${data.statusText}. ${data.status === 'ready' ? `Ready for pickup at ${data.location} by ${data.pickupTime}.` : ''} View: ${data.orderUrl}`
  })
}

// Notification delivery functions (these would typically run on the server)
export const sendSMSNotification = async (payload: SMSPayload, userId?: string, type?: string, data?: any): Promise<boolean> => {
  try {
    const response = await fetch('/.netlify/functions/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: type || 'system',
        to: payload.to,
        data: data || { message: payload.message },
        userId,
      }),
    })

    const result = await response.json()

    if (!response.ok || !result.success) {
      console.error('[SMS Send Failed]', result.error)
      return false
    }

    console.log('[SMS Sent]', { to: payload.to, messageSid: result.messageSid })
    return true
  } catch (error) {
    console.error('[SMS Send Error]', error)
    return false
  }
}

export const sendEmailNotification = async (payload: EmailPayload, userId?: string, type?: string, data?: any): Promise<boolean> => {
  try {
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: type || 'system',
        to: payload.to,
        data: data || {
          subject: payload.subject,
          html: payload.html,
          text: payload.text,
        },
        userId,
      }),
    })

    const result = await response.json()

    if (!response.ok || !result.success) {
      console.error('[Email Send Failed]', result.error)
      return false
    }

    console.log('[Email Sent]', { to: payload.to, messageId: result.messageId })
    return true
  } catch (error) {
    console.error('[Email Send Error]', error)
    return false
  }
}

export const sendPushNotification = async (payload: PushPayload): Promise<boolean> => {
  // Web Push notification
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        // This would send push notification via service worker
        console.log('Push notification would be sent:', payload)
        return true
      }
    } catch (error) {
      console.error('Push notification failed:', error)
    }
  }
  return false
}

// Main notification dispatcher
export const sendNotification = async (payload: NotificationPayload): Promise<void> => {
  try {
    // Store notification in database
    const { data: user } = await supabase
      .from('secret_users')
      .select('email, phone, notification_preferences')
      .eq('id', payload.userId)
      .single()

    if (!user) {
      console.error('User not found for notification:', payload.userId)
      return
    }

    // Create notification record
    await supabase.from('secret_notifications').insert({
      user_id: payload.userId,
      event_id: payload.eventId,
      type: payload.type,
      title: payload.title,
      message: payload.message,
      channels: payload.channels
    })

    // Send via requested channels
    const promises: Promise<boolean>[] = []

    if (payload.channels.includes('sms') && user.phone && user.notification_preferences?.sms) {
      const smsMessage = SMS_TEMPLATES[payload.type]?.(payload.metadata) || payload.message
      promises.push(sendSMSNotification(
        {
          to: user.phone,
          message: smsMessage
        },
        payload.userId,
        payload.type,
        payload.metadata
      ))
    }

    if (payload.channels.includes('email') && user.email && user.notification_preferences?.email) {
      const emailTemplate = EMAIL_TEMPLATES[payload.type]?.(payload.metadata)
      if (emailTemplate) {
        promises.push(sendEmailNotification(
          {
            to: user.email,
            subject: emailTemplate.subject,
            html: emailTemplate.html,
            text: emailTemplate.text
          },
          payload.userId,
          payload.type,
          payload.metadata
        ))
      }
    }

    if (payload.channels.includes('push') && user.notification_preferences?.push) {
      promises.push(sendPushNotification({
        userId: payload.userId,
        title: payload.title,
        body: payload.message,
        data: payload.metadata
      }))
    }

    // Execute all notification sends
    await Promise.allSettled(promises)

    // Mark as sent
    await supabase
      .from('secret_notifications')
      .update({ sent_at: new Date().toISOString() })
      .eq('user_id', payload.userId)
      .eq('type', payload.type)
      .eq('title', payload.title)

  } catch (error) {
    console.error('Failed to send notification:', error)
    throw error
  }
}

// Helper functions for common notifications
export const notifyEventAnnouncement = async (userId: string, eventData: any) => {
  await sendNotification({
    userId,
    type: 'event_announcement',
    title: `New SECRET Event: ${eventData.title}`,
    message: `${eventData.title} at ${eventData.location} on ${eventData.date}`,
    channels: ['sms', 'email', 'push'],
    eventId: eventData.id,
    metadata: eventData
  })
}

export const notifyOrderConfirmation = async (userId: string, orderData: any) => {
  await sendNotification({
    userId,
    type: 'order_confirmation',
    title: 'Order Confirmed',
    message: `Order ${orderData.orderNumber} confirmed for $${orderData.total}`,
    channels: ['sms', 'email', 'push'],
    orderId: orderData.id,
    metadata: orderData
  })
}

export const notifyOrderStatus = async (userId: string, orderData: any) => {
  await sendNotification({
    userId,
    type: 'order_status',
    title: 'Order Update',
    message: `Order ${orderData.orderNumber} is ${orderData.status}`,
    channels: ['sms', 'push'],
    orderId: orderData.id,
    metadata: orderData
  })
}

export const notifyTransferReceived = async (userId: string, transferData: any) => {
  await sendNotification({
    userId,
    type: 'transfer_received',
    title: 'Order Transferred',
    message: `You received order ${transferData.orderNumber} from ${transferData.fromName}`,
    channels: ['sms', 'email', 'push'],
    orderId: transferData.orderId,
    metadata: transferData
  })
}